import { Link } from "react-router-dom";
import { Inbox } from "lucide-react";
import { useSupportQueue } from "@/hooks/useSupportQueue";

/**
 * One line on the board for the enhancement queue (Brett, 2026-09-29):
 * enhancements stay their own queue — small, department-routed, delivered
 * through Jira — but the board shows how much is waiting so nobody has
 * to keep a second list in their head.
 */
export function EnhancementStrip() {
  const q = useSupportQueue();
  const items = (q.data ?? []).filter((i) => i.source_group === "project_management" && !(i.is_closed || i.done_manual || i.jira_status_category === "done"));
  if (!q.data) return null;
  const open = items.length;
  const unrouted = items.filter((i) => !i.department).length;
  const awaiting = items.filter((i) => i.approval_state_effective !== "approved").length;
  const ages = items.map((i) => (i.fs_created_at ? (Date.now() - new Date(i.fs_created_at).getTime()) / 86_400_000 : 0));
  const oldest = ages.length ? Math.round(Math.max(...ages)) : 0;
  return (
    <Link
      to="/top-projects?view=enhancements"
      className="flex items-center gap-3 rounded-xl border border-line bg-surface px-4 py-2.5 text-xs text-ink-3 transition-colors hover:bg-raised"
      data-testid="enhancement-strip"
      title="Small enhancement requests — routed to departments, delivered through Jira. Not ranked here."
    >
      <Inbox className="h-4 w-4 text-ink-subtle" />
      <span className="font-semibold text-ink">{open} enhancement{open === 1 ? "" : "s"} queued</span>
      {open > 0 && (
        <span className="text-ink-muted">
          · {awaiting} awaiting approval · {unrouted} not yet routed{oldest > 0 ? ` · oldest ${oldest}d` : ""}
        </span>
      )}
      <span className="ml-auto text-brand-navy-600 dark:text-brand-navy-300">Open the queue →</span>
    </Link>
  );
}
